import React, { useState } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import sample data
import { productCategoriesL1 } from '../../data/productData';
import { countries, regions } from '../../data/geoData';
import { shopCategoriesL1 } from '../../data/shopData';
import { monthlySalesByCategory, monthlySalesByRegion } from '../../data/timeData';

const Dashboard = () => {
  const [timeFrame, setTimeFrame] = useState('12m');
  
  // Prepare data for charts
  const topCategories = [...productCategoriesL1]
    .sort((a, b) => b.totalSales - a.totalSales)
    .slice(0, 5);
    
  const topRegions = [...regions]
    .sort((a, b) => b.totalSales - a.totalSales);
    
  const pieData = topCategories.map(cat => ({
    name: cat.name,
    value: cat.totalSales
  }));
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  
  // Filter time series data based on selected timeframe
  const timeSeriesData = timeFrame === '12m' 
    ? monthlySalesByCategory 
    : monthlySalesByCategory.slice(0, timeFrame === '6m' ? 6 : 3);
  
  // Calculate total sales and growth
  const totalSales = productCategoriesL1.reduce((sum, cat) => sum + cat.totalSales, 0);
  const avgGrowth = productCategoriesL1.reduce((sum, cat) => sum + cat.growth, 0) / productCategoriesL1.length;
  const totalShops = shopCategoriesL1.reduce((sum, cat) => sum + cat.shopCount, 0);
  const totalProducts = productCategoriesL1.reduce((sum, cat) => sum + cat.productCount, 0);
  
  return (
    <div className="dashboard-container">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Market Analytics Dashboard</h1>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="kpi-card">
          <h3 className="kpi-label">Total Sales</h3>
          <p className="kpi-value">${(totalSales / 1000000).toFixed(1)}M</p>
          <p className="text-sm text-green-500">↑ {avgGrowth.toFixed(1)}% growth</p>
        </div>
        <div className="kpi-card">
          <h3 className="kpi-label">Products Tracked</h3>
          <p className="kpi-value">{totalProducts.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Across {productCategoriesL1.length} categories</p>
        </div>
        <div className="kpi-card">
          <h3 className="kpi-label">Active Shops</h3>
          <p className="kpi-value">{totalShops.toLocaleString()}</p>
          <p className="text-sm text-gray-500">{countries.length} countries</p>
        </div>
        <div className="kpi-card">
          <h3 className="kpi-label">Top Category</h3>
          <p className="kpi-value">{topCategories[0].name}</p>
          <p className="text-sm text-green-500">↑ {topCategories[0].growth.toFixed(1)}% growth</p>
        </div>
      </div>
      
      {/* Time filter selector */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Sales Trends</h2>
        <div className="inline-flex">
          <button
            onClick={() => setTimeFrame('3m')}
            className={`px-3 py-1 text-sm font-medium rounded-l-md ${
              timeFrame === '3m' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            3 Months
          </button>
          <button
            onClick={() => setTimeFrame('6m')}
            className={`px-3 py-1 text-sm font-medium ${
              timeFrame === '6m' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            6 Months
          </button>
          <button
            onClick={() => setTimeFrame('12m')}
            className={`px-3 py-1 text-sm font-medium rounded-r-md ${
              timeFrame === '12m' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            12 Months
          </button>
        </div>
      </div>
      
      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Time Series Chart */}
        <div className="chart-container">
          <h3 className="chart-title">Category Sales Trends</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={timeSeriesData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(1)}M`} />
                <Legend />
                <Line type="monotone" dataKey="Electronics" stroke="#0088FE" strokeWidth={3} activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="Clothing" stroke="#00C49F" strokeWidth={3} />
                <Line type="monotone" dataKey="Home Appliances" stroke="#FFBB28" strokeWidth={3} />
                <Line type="monotone" dataKey="Beauty" stroke="#FF8042" strokeWidth={3} />
                <Line type="monotone" dataKey="Furniture" stroke="#8884D8" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Category Distribution */}
        <div className="chart-container">
          <h3 className="chart-title">Sales by Category</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(1)}M`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Categories */}
        <div className="chart-container">
          <h3 className="chart-title">Top Performing Categories</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topCategories}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 90, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" />
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(1)}M`} />
                <Legend />
                <Bar dataKey="totalSales" fill="#0088FE" name="Total Sales" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Regional Performance */}
        <div className="chart-container">
          <h3 className="chart-title">Regional Performance</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topRegions}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(1)}M`} />
                <Legend />
                <Bar dataKey="totalSales" fill="#00C49F" name="Total Sales" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;